/**
 * LeadCapture — STYLE-GUIDE / DEV PLACEHOLDER ONLY.
 *
 * ⚠️ This component is NOT wired to a backend. It is currently used only on
 * /style-guide for visual documentation. Do NOT render this on a public,
 * production-facing route — submissions will be silently dropped.
 *
 * Before using on a live page, wire `onSubmit` to:
 *   1. supabase.from("contact_submissions").insert(...)
 *   2. supabase.functions.invoke("send-contact-notification", { body: { ..., source: "Lead Capture" } })
 * and add a thank-you state matching ContactSection / BookConsultation.
 */
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight } from "lucide-react";

interface LeadCaptureProps {
  title: string;
  description?: string;
  ctaText: string;
  showEmailInput?: boolean;
  variant?: "light" | "dark" | "accent";
  onSubmit?: (email: string) => void;
}

const variantStyles = {
  light: "bg-card border border-border",
  dark: "bg-secondary on-dark",
  accent: "bg-accent/10 border-2 border-accent/30",
};

const LeadCapture = ({
  title,
  description,
  ctaText,
  showEmailInput = false,
  variant = "light",
}: LeadCaptureProps) => (
  <div className={cn("p-10 md:p-14", variantStyles[variant])}>
    <div className="max-w-xl mx-auto text-center space-y-5">
      {/* Eyebrow */}
      <span
        className={cn(
          "font-body text-[10px] uppercase tracking-[0.25em] font-semibold",
          variant === "dark" ? "text-accent" : "text-accent"
        )}
      >
        Free Resource
      </span>

      <h3
        className={cn(
          "font-headline text-2xl md:text-3xl",
          "text-foreground"
        )}
      >
        {title}
      </h3>

      {description && (
        <p
          className={cn(
            "text-sm leading-relaxed max-w-md mx-auto",
            "text-muted-foreground"
          )}
        >
          {description}
        </p>
      )}

      <div className="pt-2">
        {showEmailInput ? (
          <form
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            onSubmit={(e) => e.preventDefault()}
            aria-label="Email signup"
          >
            <label htmlFor="lead-email" className="sr-only">Email address</label>
            <Input
              id="lead-email"
              type="email"
              placeholder="Enter your email"
              className="flex-1 h-12"
              aria-required="true"
            />
            <Button variant="gold" size="lg" className="shrink-0 gap-2">
              {ctaText}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </form>
        ) : (
          <Button variant="gold" size="lg" className="gap-2">
            {ctaText}
            <ArrowRight className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  </div>
);

export default LeadCapture;
